/**
 * CategoryBlock Component
 * 
 * Displays a category with its menu items.
 * Items are fetched from the database using the categoryId reference.
 * Operational data (prices, availability) always comes from the database.
 * 
 * ENHANCEMENT (January 2026):
 * - Added popup modal for full item details with variant selection
 */

import React, { memo, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { CategoryBlockProps } from '@/types/menuBuilder';
import { useCachedMenuItems } from '@/hooks/useCache';
import { useCachedCategories } from '@/hooks/useCache';
import { useBranch } from '@/contexts/BranchContext';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { formatCurrency } from '@/utils/currencyUtils';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { Branch, MenuItem } from '@/types/index';
import { Button } from '@/components/ui/button';
import { Plus, Minus, Star, ShoppingCart } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useMenuItemRatings } from '@/hooks/useMenuItemRatings';
import { PublicMenuData } from '@/hooks/usePublicMenuFullData';
import { usePublicMenuVariants, VariantsMap } from '@/hooks/usePublicMenuVariants';
import { MenuItemVariant } from '@/types/index';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMenuBuilderStore } from '@/stores/menuBuilderStore';

interface CategoryBlockComponentProps {
  props: CategoryBlockProps;
  isPreview?: boolean;
  branch?: Branch;
  enableRatings?: boolean;
  enableOrdering?: boolean;
  preloadedData?: PublicMenuData;
}

export const CategoryBlock = memo(function CategoryBlock({
  props,
  isPreview = false,
  branch,
  enableRatings = false,
  enableOrdering = false,
  preloadedData,
}: CategoryBlockComponentProps) {
  const { t, i18n } = useTranslation();
  const { selectedBranchId } = useBranch(true);
  const { branches } = useFilteredBranches();

  // Get global theme for font weight settings
  const builderTheme = useMenuBuilderStore(s => s.globalThemeOverrides);
  const globalTheme = preloadedData?.layout?.globalThemeOverrides || builderTheme;
  const isBold = globalTheme?.fontWeight === 'bold';
  
  const currentBranch = useMemo(() => {
    if (branch) return branch;
    return branches.find(b => b.id === selectedBranchId) || null;
  }, [branch, branches, selectedBranchId]);
  
  const currency = currentBranch?.currency || 'USD';
  
  // Get cart button color from layout's global theme overrides
  const cartButtonColor = useMemo(() => {
    return preloadedData?.layout?.globalThemeOverrides?.cartButtonColor 
      || currentBranch?.accent_color 
      || currentBranch?.primary_color 
      || '#3b82f6';
  }, [preloadedData?.layout?.globalThemeOverrides?.cartButtonColor, currentBranch?.accent_color, currentBranch?.primary_color]);
  
  const { addItem, items: cartItems, updateQuantity, removeItem } = useCart();

  const {
    categoryId,
    layout,
    columns,
    showPrices,
    showDescriptions,
    showImages,
    showTitle,
    maxItems,
    itemShape,
    titleFontSize,
    customTitleFontSize,
    titleColor,
    titleAlignment,
    itemNameColor,
    itemDescriptionColor,
    itemPriceColor,
  } = props;

  // Fetch category data
  const { data: categoriesData, loading: categoriesLoading } = useCachedCategories(
    !preloadedData ? (currentBranch?.id || '') : ''
  );
  
  const categories = preloadedData?.categories || categoriesData || [];

  // Find the selected category
  const category = useMemo(
    () => categories.find((c: any) => c.id === categoryId),
    [categories, categoryId]
  );

  // Fetch menu items for this category
  const { data: allItemsData, loading: itemsLoading } = useCachedMenuItems(
    !preloadedData ? (categoryId || '') : ''
  );
  
  const allItems = useMemo(() => {
    if (preloadedData?.menuItems) {
      return preloadedData.menuItems.filter((item: any) => item.category_id === categoryId);
    }
    return allItemsData || [];
  }, [preloadedData, allItemsData, categoryId]);

  // Filter to only available items and apply maxItems limit
  const items = useMemo(() => {
    const available = allItems.filter((item: any) => item.is_available !== false);
    return maxItems ? available.slice(0, maxItems) : available;
  }, [allItems, maxItems]);

  // Batch fetch variants for all items
  const menuItemIds = useMemo(() => items.map((item: any) => item.id), [items]);
  const { variantsMap: fetchedVariantsMap } = usePublicMenuVariants(
    !preloadedData ? (currentBranch?.id || undefined) : undefined, 
    menuItemIds
  );

  // Build variants map from preloaded data or fetched data
  const variantsMap: VariantsMap = useMemo(() => {
    if (preloadedData?.variants) {
      const map: VariantsMap = {};
      preloadedData.variants.forEach((variant: MenuItemVariant) => {
        if (!map[variant.menu_item_id]) {
          map[variant.menu_item_id] = [];
        }
        map[variant.menu_item_id].push(variant);
      });
      return map;
    }
    return fetchedVariantsMap;
  }, [preloadedData, fetchedVariantsMap]);

  // State for item detail popup
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);

  // Helper to get item variants
  const getItemVariants = (itemId: string): MenuItemVariant[] => {
    return variantsMap[itemId] || [];
  };

  // Get selected variant for modal
  const selectedItemVariants = selectedItem ? getItemVariants(selectedItem.id) : [];
  const selectedVariant = selectedVariantId 
    ? selectedItemVariants.find(v => v.id === selectedVariantId) 
    : null;

  // Get current price for modal
  const modalCurrentPrice = selectedItem 
    ? (selectedVariant ? selectedVariant.price : selectedItem.base_price)
    : 0;

  // Get current description for modal
  const modalCurrentDescription = selectedVariant?.description || selectedItem?.description;

  // Get cart item for modal quantity
  const modalCartItem = selectedItem ? cartItems.find(ci => ci.menuItem.id === selectedItem.id) : null;
  const modalQuantity = modalCartItem?.quantity || 0;

  // Handle item click to open popup
  const handleItemClick = (item: MenuItem) => {
    setSelectedItem(item);
    setSelectedVariantId(null); // Reset to base price
    setIsDetailModalOpen(true);
  };

  // Handle add to cart from modal
  const handleModalAddToCart = () => {
    if (selectedItem) {
      addItem(selectedItem, selectedVariant || undefined);
    }
  };

  // Handle quantity change from modal
  const handleModalQuantityChange = (newQuantity: number) => {
    if (!selectedItem) return;
    if (newQuantity <= 0) {
      removeItem(selectedItem.id);
    } else {
      updateQuantity(selectedItem.id, newQuantity);
    }
  };

  // Helper to format price without zeros
  const formatPriceNoZeros = (price: number): string => {
    return price % 1 === 0
      ? price.toLocaleString('en-US', { maximumFractionDigits: 0 })
      : price.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  // Helper to format price display with variants
  const formatItemPrice = (item: any): string => {
    const variants = getItemVariants(item.id);
    if (variants.length > 0) {
      const prices = variants.map(v => v.price);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      if (minPrice === maxPrice) {
        return formatCurrency(minPrice, currency);
      }
      return `${formatCurrency(minPrice, currency)} - ${formatCurrency(maxPrice, currency)}`;
    }
    return formatCurrency(item.base_price, currency);
  };

  const isLoading = !preloadedData && (categoriesLoading || itemsLoading);

  const fontSizeClasses: Record<string, string> = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
    xl: 'text-3xl',
    '2xl': 'text-4xl',
    custom: '',
  };

  const alignmentClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  // No category selected - show placeholder
  if (!categoryId) {
    return (
      <div className="p-6 text-center text-muted-foreground bg-muted/30 rounded-lg">
        {isPreview ? null : t('menuBuilder.selectCategory', 'Select a category in properties')}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        {showTitle && <Skeleton className="h-8 w-48" />}
        <div className={cn(
          'grid gap-4',
          layout === 'grid' && `grid-cols-${columns || 2}`,
          layout === 'list' && 'grid-cols-1'
        )}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="p-6 text-center text-muted-foreground bg-destructive/10 rounded-lg">
        {t('menuBuilder.categoryNotFound', 'Category not found')}
      </div>
    );
  }

  return (
    <div id={categoryId} className="p-4 space-y-4 scroll-mt-20">
      {/* Category Title */}
      {showTitle && (
        <div className={cn("flex items-center gap-2 mb-4 flex-wrap", alignmentClasses[titleAlignment || 'left'])}>
          <h2 
            className={cn(
              isBold ? "font-bold" : "font-semibold", 
              fontSizeClasses[titleFontSize || 'md']
            )}
            style={{
              color: titleColor,
              fontSize: titleFontSize === 'custom' ? `${customTitleFontSize}px` : undefined
            }}
          >
            {category.name}
          </h2>
          {category.description && (
            <span className="text-sm text-muted-foreground">
              {category.description}
            </span>
          )}
        </div>
      )}

      {/* Items */}
      {items.length === 0 ? (
        <div className="p-4 text-center text-muted-foreground bg-muted/30 rounded-lg">
          {t('menuBuilder.noItems', 'No items in this category')}
        </div>
      ) : layout === 'carousel' ? (
        // Carousel mode - simple list (carousel functionality can be enhanced)
        <div className="space-y-3">
          {items.map((item) => {
            const cartItem = cartItems.find(i => i.menuItem.id === item.id);
            const itemVariants = getItemVariants(item.id);
            return (
            <div
              key={item.id}
              className="flex items-start justify-between py-2 border-b border-dashed last:border-0 gap-4 cursor-pointer hover:bg-muted/30 transition-colors rounded-lg px-2 -mx-2"
              onClick={() => handleItemClick(item)}
            >
              <div className="flex-1 min-w-0">
                <span 
                  className={cn(
                    isBold ? 'font-bold' : 'font-medium',
                    !item.is_available && 'line-through text-muted-foreground'
                  )}
                  style={itemNameColor && item.is_available ? { color: itemNameColor } : undefined}
                >
                  {item.name}
                </span>
                {showDescriptions && item.description && (
                  <p 
                    className="text-sm text-muted-foreground mt-0.5 line-clamp-2"
                    style={itemDescriptionColor ? { color: itemDescriptionColor } : undefined}
                  >
                    {item.description}
                  </p>
                )}
                {/* Show variants if available */}
                {itemVariants.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {itemVariants.map(variant => (
                      <span 
                        key={variant.id} 
                        className="text-xs bg-muted px-2 py-0.5 rounded-full cursor-help"
                        title={variant.description || undefined}
                      >
                        {variant.size_name}: {formatCurrency(variant.price, currency)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {showPrices && itemVariants.length === 0 && (
                  <span 
                    className={isBold ? "font-bold" : "font-medium"}
                    style={itemPriceColor ? { color: itemPriceColor } : undefined}
                  >
                    {formatCurrency(item.base_price, currency)}
                  </span>
                )}
                {enableOrdering && item.is_available && (
                  cartItem ? (
                    <div className="flex items-center gap-1 bg-secondary/50 rounded-full px-1 h-8" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-6 w-6 rounded-full hover:bg-background/80" 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (cartItem.quantity <= 1) {
                            removeItem(item.id);
                          } else {
                            updateQuantity(item.id, cartItem.quantity - 1);
                          }
                        }}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-sm font-medium w-4 text-center">{cartItem.quantity}</span>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-6 w-6 rounded-full hover:bg-background/80" 
                        onClick={(e) => {
                          e.stopPropagation();
                          updateQuantity(item.id, cartItem.quantity + 1);
                        }}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8 w-8 p-0 rounded-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        addItem(item);
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )
                )}
              </div>
            </div>
          )})}
        </div>
      ) : (
        // Grid or List mode
        <div
          className={cn(
            'grid gap-4',
            layout === 'grid' && columns === 2 && 'grid-cols-2',
            layout === 'grid' && columns === 3 && 'grid-cols-3',
            layout === 'list' && 'grid-cols-1'
          )}
        >
          {items.map((item) => {
            const cartItem = cartItems.find(i => i.menuItem.id === item.id);
            return (
            <div
              key={item.id}
              className={cn(
                'border bg-card overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition-shadow',
                // Shape classes
                (!itemShape || itemShape === 'rounded') && 'rounded-xl',
                itemShape === 'square' && 'rounded-none',
                itemShape === 'pill' && 'rounded-[2rem]',
                itemShape === 'leaf' && 'rounded-tl-[2rem] rounded-br-[2rem] rounded-tr-sm rounded-bl-sm',
                itemShape === 'arch' && 'rounded-t-[2rem] rounded-b-lg',

                layout === 'list' && 'flex',
                !item.is_available && 'opacity-60'
              )}
              onClick={() => handleItemClick(item)}
            >
              {/* Item Image */}
              {showImages && (
                <div
                  className={cn(
                    'bg-muted',
                    layout === 'grid' && 'aspect-square',
                    layout === 'list' && 'w-24 h-24 flex-shrink-0'
                  )}
                >
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                      🍽️
                    </div>
                  )}
                </div>
              )}

              {/* Item Details */}
              <div className="p-3 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 
                    className={cn("line-clamp-1 flex-1", isBold ? "font-bold" : "font-medium")}
                    style={itemNameColor ? { color: itemNameColor } : undefined}
                  >
                    {item.name}
                  </h3>
                  {!item.is_available && (
                    <Badge variant="secondary" className="text-xs">
                      {t('common.unavailable', 'Unavailable')}
                    </Badge>
                  )}
                </div>

                {showDescriptions && item.description && (
                  <p 
                    className="text-sm text-muted-foreground mt-1 line-clamp-2"
                    style={itemDescriptionColor ? { color: itemDescriptionColor } : undefined}
                  >
                    {item.description}
                  </p>
                )}

                {/* Show variants if available */}
                {(() => {
                  const itemVariants = getItemVariants(item.id);
                  return itemVariants.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {itemVariants.map(variant => (
                        <span 
                          key={variant.id} 
                          className="text-xs bg-muted px-2 py-0.5 rounded-full cursor-help"
                          style={itemPriceColor ? { color: itemPriceColor } : undefined}
                          title={variant.description || undefined}
                        >
                          {variant.size_name}: {formatCurrency(variant.price, currency)}
                        </span>
                      ))}
                    </div>
                  ) : null;
                })()}

                {showPrices && (
                  <div className="flex items-center justify-between mt-2">
                    <p 
                      className="font-semibold"
                      style={itemPriceColor ? { color: itemPriceColor } : undefined}
                    >
                      {formatItemPrice(item)}
                    </p>
                    
                    {enableOrdering && item.is_available && (
                      cartItem ? (
                        <div className="flex items-center gap-1 bg-secondary/50 rounded-full px-1 h-8" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-6 w-6 rounded-full hover:bg-background/80" 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (cartItem.quantity <= 1) {
                                removeItem(item.id);
                              } else {
                                updateQuantity(item.id, cartItem.quantity - 1);
                              }
                            }}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="text-sm font-medium w-4 text-center">{cartItem.quantity}</span>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-6 w-6 rounded-full hover:bg-background/80" 
                            onClick={(e) => {
                              e.stopPropagation();
                              updateQuantity(item.id, cartItem.quantity + 1);
                            }}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          className="h-8 w-8 p-0 rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            addItem(item);
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )
                    )}
                  </div>
                )}
                
                {enableRatings && (
                  <div className="mt-1 flex items-center gap-1">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    <span className="text-xs text-muted-foreground">4.8 (12)</span>
                  </div>
                )}
              </div>
            </div>
          )})}
        </div>
      )}

      {/* Item Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent 
          className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto p-0" 
          dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{selectedItem?.name || 'Item Details'}</DialogTitle>
            <DialogDescription>{selectedItem?.description || 'Item details'}</DialogDescription>
          </DialogHeader>

          {/* Item Image */}
          {selectedItem?.image_url && (
            <div className="relative w-full aspect-[4/3] bg-gray-100">
              <img
                src={selectedItem.image_url}
                alt={selectedItem.name}
                className="w-full h-full object-cover"
              />
              {!selectedItem.is_available && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                    {t('dashboard.menu.unavailable') || 'Unavailable'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Content */}
          {selectedItem && (
            <div className="p-5 space-y-4">
              {/* Title and Price */}
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-xl font-bold text-gray-900 flex-1">
                  {selectedItem.name}
                </h2>
                <p 
                  className="text-xl font-bold flex-shrink-0"
                  style={{ color: cartButtonColor }}
                >
                  <span className="notranslate" lang="en">
                    {formatPriceNoZeros(modalCurrentPrice)}
                  </span>
                  <span className="text-sm font-medium ms-1">{currency}</span>
                </p>
              </div>

              {/* Full Description */}
              {modalCurrentDescription && (
                <p className="text-gray-600 text-sm leading-relaxed">
                  {modalCurrentDescription}
                </p>
              )}

              {/* Variants Selection */}
              {selectedItemVariants.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">
                    {t('dashboard.menu_item.choose_size') || 'Choose size'}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {/* Base price option (Regular/Default) */}
                    <button
                      type="button"
                      className={cn(
                        "flex flex-col items-center px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all duration-200",
                        selectedVariantId === null 
                          ? 'text-white border-transparent shadow-md' 
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      )}
                      style={selectedVariantId === null 
                        ? { backgroundColor: cartButtonColor } 
                        : { color: cartButtonColor }
                      }
                      onClick={() => setSelectedVariantId(null)}
                    >
                      <span className="font-semibold">{t('dashboard.menu_item.regular') || 'Regular'}</span>
                      <span className="text-xs opacity-80 notranslate" lang="en">
                        {formatPriceNoZeros(selectedItem.base_price)} {currency}
                      </span>
                    </button>
                    
                    {/* Other variant options */}
                    {selectedItemVariants.map(variant => (
                      <button
                        key={variant.id}
                        type="button"
                        className={cn(
                          "flex flex-col items-center px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all duration-200",
                          selectedVariantId === variant.id 
                            ? 'text-white border-transparent shadow-md' 
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        )}
                        style={selectedVariantId === variant.id 
                          ? { backgroundColor: cartButtonColor } 
                          : { color: cartButtonColor }
                        }
                        onClick={() => setSelectedVariantId(variant.id)}
                      >
                        <span className="font-semibold">{variant.size_name}</span>
                        <span className="text-xs opacity-80 notranslate" lang="en">
                          {formatPriceNoZeros(variant.price)} {currency}
                        </span>
                      </button>
                    ))}
                  </div>
                  
                </div>
              )}

              {/* Add to Cart Section */}
              {enableOrdering && selectedItem.is_available !== false && (
                <div className="pt-4 border-t">
                  {modalQuantity > 0 ? (
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex items-center gap-3 rounded-full px-2 py-1"
                        style={{ backgroundColor: `${cartButtonColor}15` }}
                      >
                        <button
                          onClick={() => handleModalQuantityChange(modalQuantity - 1)}
                          className="p-2 rounded-full hover:bg-white/50 transition-colors"
                          aria-label={t('cart.decrease_quantity') || 'Decrease quantity'}
                        >
                          <Minus className="h-5 w-5" style={{ color: cartButtonColor }} />
                        </button>
                        <span className="text-lg font-bold w-8 text-center" style={{ color: cartButtonColor }}>
                          {modalQuantity}
                        </span>
                        <button
                          onClick={() => handleModalQuantityChange(modalQuantity + 1)}
                          className="p-2 rounded-full hover:bg-white/50 transition-colors"
                          aria-label={t('cart.increase_quantity') || 'Increase quantity'}
                        >
                          <Plus className="h-5 w-5" style={{ color: cartButtonColor }} />
                        </button>
                      </div>
                      <Button
                        onClick={() => setIsDetailModalOpen(false)}
                        className="px-6"
                        style={{ backgroundColor: cartButtonColor }}
                      >
                        {t('common.done') || 'Done'}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleModalAddToCart}
                      className="w-full py-6 text-base font-semibold gap-2"
                      style={{ backgroundColor: cartButtonColor }}
                    >
                      <ShoppingCart className="h-5 w-5" />
                      {t('dashboard.menu_item.add_to_cart', 'Add to Cart')}
                    </Button>
                  )}
                </div>
              )}

              {/* Unavailable message */}
              {selectedItem.is_available === false && (
                <div className="pt-4 border-t">
                  <div className="bg-red-50 text-red-600 rounded-lg p-4 text-center text-sm font-medium">
                    {t('dashboard.menu.item_unavailable_message') || 'This item is currently unavailable'}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default CategoryBlock;
